import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Lock, TrendingDown, TrendingUp } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import WorkChart, { type WorkSeries } from '../../components/nursery/work/WorkChart';
import { NEUTRAL, SERIES_COLOURS } from '../../components/nursery/work/workColours';
import nurseryService from '../../services/nurseryWorkStats';
import { useStockSeat } from '../../hooks/useStockSeat';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { displayLocale } from '../../i18n/locale';
import { cn } from '../../utils/cn';
import {
  MAX_DAYS, bucketLabel, changePct, fitBucket, foldSeries, hourKeyText, hourOfDayLabel, presetRange,
  type NamedSeries, type RangePreset,
} from '../../utils/workStats';
import type { PickForecast, PickForecastDay, WorkBucket, WorkStats } from '../../types/nurseryWorkStats';

/**
 * /nursery/work — what the nursery's people got done, and what is still
 * ahead (Rob, 2026-10-15). Picking first: trees picked per person and per
 * farm by hour, day or week, and a forecast of the trees still to pick on
 * the loads due in the coming days against the pace of the last four weeks.
 * Budding and other work can join as further work types.
 *
 * The server does the arithmetic (HHNodeServer nurseryWorkStatsService);
 * this page only arranges it. Nursery admin seat (Retail Manager too).
 */

type Tab = 'history' | 'forecast';
type View = 'total' | 'people' | 'farms';

const PRESETS: RangePreset[] = ['today', 'yesterday', 'week', 'last7', 'last30', 'last12w', 'custom'];
const KEEP = 6;
const fmt = (n: number) => n.toLocaleString(displayLocale());

function weekdayName(dow: number): string {
  // 2026-10-11 is a Sunday.
  return new Intl.DateTimeFormat(displayLocale(), { weekday: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2026, 9, 11 + dow, 12)));
}

function dayName(date: string): string {
  return new Intl.DateTimeFormat(displayLocale(), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`));
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: ReactNode; tone?: 'bad' | 'good' }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3 min-w-0">
      <p className="text-xs text-neutral-500 break-words">{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold tabular-nums break-words',
        tone === 'bad' ? 'text-red-700' : 'text-neutral-900')}>{value}</p>
      {sub && <div className="mt-0.5 text-xs text-neutral-500 break-words">{sub}</div>}
    </div>
  );
}

function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: { value: T; label: string; disabled?: boolean }[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex flex-wrap rounded-lg border border-neutral-300 bg-white p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={o.disabled}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn('rounded-md px-2.5 py-1 text-sm',
            value === o.value ? 'bg-primary-600 text-white' : 'text-neutral-700 hover:bg-neutral-100',
            o.disabled && 'opacity-40 cursor-not-allowed')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function WorkSummary() {
  const { t } = useTranslation('nursery', { keyPrefix: 'WorkSummary' });
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const { hasAccess, loading: seatLoading } = useStockSeat('admin');
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'forecast' ? 'forecast' : 'history';
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  const [farmId, setFarmId] = useState<number | null>(null);
  const [farmOptions, setFarmOptions] = useState<{ farm_id: number; name: string }[]>([]);

  // ── History ──
  const [preset, setPreset] = useState<RangePreset>('today');
  const [range, setRange] = useState(() => presetRange('today', today));
  const [stats, setStats] = useState<WorkStats | null>(null);
  const [view, setView] = useState<View>('total');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choosePreset = (p: RangePreset) => {
    setPreset(p);
    if (p !== 'custom') setRange(presetRange(p, today));
  };
  const setDates = (from: string, to: string) => {
    if (!from || !to) return;
    setRange((r) => ({ from, to, bucket: fitBucket(from, to, r.bucket) }));
  };
  const setBucket = (bucket: WorkBucket) => setRange((r) => ({ ...r, bucket }));

  const loadStats = useCallback(async () => {
    if (range.to < range.from) { setError(t('endBeforeStart')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getWorkStats({ ...range, farm_id: farmId });
      if (res.success) {
        setStats(res.data);
        if (res.data.farm_options?.length) setFarmOptions(res.data.farm_options);
      } else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [range, farmId, t]);

  useEffect(() => { if (hasAccess && tab === 'history') loadStats(); }, [hasAccess, tab, loadStats]);

  // ── Forecast ──
  const [days, setDays] = useState(14);
  const [forecast, setForecast] = useState<PickForecast | null>(null);
  const [fLoading, setFLoading] = useState(false);
  const [fError, setFError] = useState<string | null>(null);

  const loadForecast = useCallback(async () => {
    setFLoading(true);
    setFError(null);
    try {
      const res = await nurseryService.getPickForecast({ days, farm_id: farmId });
      if (res.success) setForecast(res.data);
      else setFError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setFError(errorMessage(e, t('loadFailed')));
    } finally {
      setFLoading(false);
    }
  }, [days, farmId, t]);

  useEffect(() => { if (hasAccess && tab === 'forecast') loadForecast(); }, [hasAccess, tab, loadForecast]);

  // Farm names for the filter: from either payload.
  useEffect(() => {
    if (farmOptions.length || !forecast) return;
    setFarmOptions(forecast.farms.filter((f) => f.name).map((f) => ({ farm_id: f.farm_id, name: f.name as string })));
  }, [forecast, farmOptions.length]);

  if (seatLoading) {
    return <div className="max-w-6xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenseTitle')}</h2>
          <p className="text-sm text-neutral-500">{t('licenseBody')}</p>
        </div>
      </div>
    );
  }

  const farmSelect = (
    <label className="block text-sm min-w-0">
      <span className="text-neutral-700">{t('farm')}</span>
      <select
        className="block w-full sm:w-auto max-w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
        value={farmId ?? ''}
        onChange={(e) => setFarmId(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{t('allFarms')}</option>
        {farmOptions.map((f) => <option key={f.farm_id} value={f.farm_id}>{f.name}</option>)}
      </select>
    </label>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-700">{t('kindPicking')}</span>
        <span className="text-xs text-neutral-500">{t('kindMore')}</span>
      </div>

      <div role="tablist" className="flex gap-1 border-b border-neutral-200">
        {(['history', 'forecast'] as Tab[]).map((k) => (
          <button
            key={k}
            role="tab"
            type="button"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium',
              tab === k ? 'border-primary-600 text-primary-700' : 'border-transparent text-neutral-500 hover:text-neutral-700')}
          >
            {t(`tab_${k}`)}
          </button>
        ))}
      </div>

      {tab === 'history' ? (
        <History
          stats={stats} loading={loading} error={error} preset={preset} range={range} view={view} today={today}
          onPreset={choosePreset} onDates={setDates} onBucket={setBucket} onView={setView} farmSelect={farmSelect}
        />
      ) : (
        <Forecast
          forecast={forecast} loading={fLoading} error={fError} days={days} onDays={setDays} farmSelect={farmSelect}
        />
      )}
    </div>
  );
}

// ── History tab ─────────────────────────────────────────────────────────────

function History({
  stats, loading, error, preset, range, view, today, onPreset, onDates, onBucket, onView, farmSelect,
}: {
  stats: WorkStats | null; loading: boolean; error: string | null; preset: RangePreset;
  range: { from: string; to: string; bucket: WorkBucket }; view: View; today: string;
  onPreset: (p: RangePreset) => void; onDates: (from: string, to: string) => void;
  onBucket: (b: WorkBucket) => void; onView: (v: View) => void; farmSelect: ReactNode;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'WorkSummary' });
  const days = Math.round((Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86400000) + 1;

  const labels = useMemo(
    () => (stats ? stats.buckets.map((k) => bucketLabel(k, stats.bucket, stats.from !== stats.to)) : []),
    [stats],
  );

  // Colours follow the person or farm (their place in the full ranking for
  // the range), never the filter.
  const chartSeries: WorkSeries[] = useMemo(() => {
    if (!stats) return [];
    if (view === 'total') return [{ label: t('treesPicked'), data: stats.series, colour: SERIES_COLOURS[0], kind: stats.bucket === 'hour' ? 'bar' : 'line' }];
    const items: NamedSeries[] = view === 'people'
      ? stats.pickers.map((p) => ({ key: `u${p.user_id}`, label: p.name ?? t('unknownPerson'), series: p.series, total: p.trees }))
      : stats.farms.map((f) => ({ key: `f${f.farm_id}`, label: f.name ?? t('unknownFarm'), series: f.series, total: f.trees }));
    return foldSeries(items, KEEP, t('others')).map((s, i) => ({
      label: s.label, data: s.series, colour: s.key === 'others' ? NEUTRAL : SERIES_COLOURS[i % SERIES_COLOURS.length],
      kind: stats.bucket === 'hour' ? 'bar' : 'line',
    }));
  }, [stats, view, t]);

  const hourLabels = useMemo(() => Array.from({ length: 24 }, (_, h) => hourOfDayLabel(h)), []);
  const hourSeries: WorkSeries[] = useMemo(() => (stats
    ? [{ label: t('treesPicked'), data: stats.by_hour_of_day, colour: SERIES_COLOURS[0], kind: 'bar' }]
    : []), [stats, t]);
  // Trim the night either side so the working day fills the chart.
  const hourWindow = useMemo(() => {
    if (!stats) return { labels: hourLabels, series: hourSeries };
    const nz = stats.by_hour_of_day.map((n, i) => (n > 0 ? i : -1)).filter((i) => i >= 0);
    if (!nz.length) return { labels: hourLabels, series: hourSeries };
    const a = Math.max(0, nz[0] - 1);
    const b = Math.min(23, nz[nz.length - 1] + 1);
    return { labels: hourLabels.slice(a, b + 1), series: hourSeries.map((s) => ({ ...s, data: s.data.slice(a, b + 1) })) };
  }, [stats, hourLabels, hourSeries]);

  const totalHours = stats ? stats.pickers.reduce((s, p) => s + p.hours, 0) : 0;
  const change = stats ? changePct(stats.totals.trees, stats.previous.trees) : null;
  const farmName = (id: number) => stats?.farms.find((f) => f.farm_id === id)?.name ?? t('unknownFarm');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-sm min-w-0">
          <span className="text-neutral-700">{t('period')}</span>
          <select
            className="block w-full sm:w-auto max-w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
            value={preset}
            onChange={(e) => onPreset(e.target.value as RangePreset)}
          >
            {PRESETS.map((p) => <option key={p} value={p}>{t(`preset_${p}`)}</option>)}
          </select>
        </label>
        {preset === 'custom' && (
          <>
            <label className="block text-sm">
              <span className="text-neutral-700">{t('from')}</span>
              <input type="date" max={today} className="block rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
                value={range.from} onChange={(e) => onDates(e.target.value, range.to)} />
            </label>
            <label className="block text-sm">
              <span className="text-neutral-700">{t('to')}</span>
              <input type="date" max={today} className="block rounded-lg border border-neutral-300 px-2 py-1.5 text-sm"
                value={range.to} onChange={(e) => onDates(range.from, e.target.value)} />
            </label>
          </>
        )}
        <div className="text-sm">
          <span className="block text-neutral-700">{t('groupBy')}</span>
          <Segmented
            label={t('groupBy')}
            value={range.bucket}
            onChange={onBucket}
            options={(['hour', 'day', 'week'] as WorkBucket[]).map((b) => ({ value: b, label: t(`bucket_${b}`), disabled: days > MAX_DAYS[b] }))}
          />
        </div>
        {farmSelect}
      </div>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {loading && !stats && <div className="h-64 rounded-xl bg-neutral-100 animate-pulse" />}

      {stats && (
        <div className={cn('space-y-5', loading && 'opacity-60')}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile
              label={t('treesPicked')}
              value={fmt(stats.totals.trees)}
              sub={change == null ? t('noPrevious') : (
                <span className="inline-flex items-center gap-1">
                  {change >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {t('vsPrevious', { pct: `${change > 0 ? '+' : ''}${change}`, n: fmt(stats.previous.trees) })}
                </span>
              )}
            />
            <Tile label={t('pickers')} value={fmt(stats.totals.pickers)} sub={t('farmsCount', { count: stats.totals.farms })} />
            <Tile
              label={t('perPickerHour')}
              value={totalHours ? fmt(Math.round(stats.totals.trees / totalHours)) : '–'}
              sub={t('pickerHours', { n: fmt(totalHours) })}
            />
            <Tile label={t('ordersLoads')} value={fmt(stats.totals.orders)} sub={t('loadsCount', { count: stats.totals.loads })} />
            <Tile
              label={t(`busiest_${stats.bucket}`)}
              value={stats.totals.busiest ? fmt(stats.totals.busiest.trees) : '–'}
              sub={stats.totals.busiest ? bucketLabel(stats.totals.busiest.bucket, stats.bucket, stats.from !== stats.to) : t('nothingPicked')}
            />
          </div>

          <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-neutral-900">{t(`trendTitle_${stats.bucket}`)}</h2>
              <Segmented
                label={t('show')}
                value={view}
                onChange={onView}
                options={(['total', 'people', 'farms'] as View[]).map((v) => ({ value: v, label: t(`view_${v}`) }))}
              />
            </div>
            {stats.totals.trees === 0 ? (
              <p className="py-10 text-center text-sm text-neutral-500">{t('nothingPicked')}</p>
            ) : (
              <WorkChart labels={labels} series={chartSeries} stacked={view !== 'total' && stats.bucket === 'hour'}
                ariaLabel={t('trendAria', { n: fmt(stats.totals.trees) })} />
            )}
            {view !== 'total' && chartSeries.length > KEEP && <p className="text-xs text-neutral-500">{t('othersNote', { count: KEEP })}</p>}
            <details className="text-sm">
              <summary className="cursor-pointer text-xs text-primary-600">{t('showTable')}</summary>
              <div className="mt-2 max-h-72 overflow-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-neutral-500">
                    <th className="py-1 font-medium">{t(`bucket_${stats.bucket}`)}</th>
                    <th className="py-1 text-right font-medium">{t('trees')}</th>
                  </tr></thead>
                  <tbody>
                    {stats.buckets.map((k, i) => stats.series[i] > 0 && (
                      <tr key={k} className="border-t border-neutral-100">
                        <td className="py-1">{labels[i]}</td>
                        <td className="py-1 text-right tabular-nums">{fmt(stats.series[i])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </section>

          {stats.totals.trees > 0 && !(stats.bucket === 'hour' && stats.from === stats.to) && (
            <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-2">
              <h2 className="text-base font-semibold text-neutral-900">{t('hourOfDayTitle')}</h2>
              <p className="text-xs text-neutral-500">{t('hourOfDayBody')}</p>
              <WorkChart labels={hourWindow.labels} series={hourWindow.series} height="h-44" ariaLabel={t('hourOfDayTitle')} />
            </section>
          )}

          <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
            <h2 className="text-base font-semibold text-neutral-900">{t('peopleTitle')}</h2>
            {stats.pickers.length === 0 ? <p className="text-sm text-neutral-500">{t('nothingPicked')}</p> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[36rem] text-sm">
                  <thead><tr className="text-left text-xs text-neutral-500">
                    <th className="py-1.5 font-medium">{t('person')}</th>
                    <th className="py-1.5 text-right font-medium">{t('trees')}</th>
                    <th className="py-1.5 text-right font-medium">{t('share')}</th>
                    <th className="py-1.5 text-right font-medium">{t('hours')}</th>
                    <th className="py-1.5 text-right font-medium">{t('perHour')}</th>
                    <th className="py-1.5 pl-3 font-medium">{t('firstLast')}</th>
                    <th className="py-1.5 pl-3 font-medium">{t('farms')}</th>
                  </tr></thead>
                  <tbody>
                    {stats.pickers.map((p) => {
                      const share = stats.totals.trees ? Math.round((p.trees / stats.totals.trees) * 100) : 0;
                      return (
                        <tr key={p.user_id ?? 'none'} className="border-t border-neutral-100 align-top">
                          <td className="py-1.5 font-medium text-neutral-900">{p.name ?? t('unknownPerson')}</td>
                          <td className="py-1.5 text-right tabular-nums">{fmt(p.trees)}</td>
                          <td className="py-1.5 text-right">
                            <span className="inline-flex items-center justify-end gap-1.5">
                              <span className="hidden sm:inline-block h-1.5 w-16 rounded-full bg-neutral-100" aria-hidden="true">
                                <span className="block h-1.5 rounded-full bg-primary-500" style={{ width: `${share}%` }} />
                              </span>
                              <span className="tabular-nums">{share}%</span>
                            </span>
                          </td>
                          <td className="py-1.5 text-right tabular-nums">{fmt(p.hours)}</td>
                          <td className="py-1.5 text-right tabular-nums">{fmt(p.trees_per_hour)}</td>
                          <td className="py-1.5 pl-3 text-xs text-neutral-600">
                            {hourKeyText(p.first_hour)}{p.last_hour !== p.first_hour && <> – {hourKeyText(p.last_hour)}</>}
                          </td>
                          <td className="py-1.5 pl-3 text-xs text-neutral-600">
                            {p.farms.map((f) => `${farmName(f.farm_id)} ${fmt(f.trees)}`).join(' · ')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-neutral-500">{t('hoursNote')}</p>
          </section>

          {stats.farms.length > 0 && (
            <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
              <h2 className="text-base font-semibold text-neutral-900">{t('farmsTitle')}</h2>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {stats.farms.map((f) => (
                  <li key={f.farm_id} className="rounded-lg border border-neutral-200 p-3 min-w-0">
                    <p className="font-medium text-neutral-900 break-words">{f.name ?? t('unknownFarm')}</p>
                    <p className="text-2xl font-semibold tabular-nums">{fmt(f.trees)}</p>
                    <p className="text-xs text-neutral-500">{t('farmLine', { count: f.pickers, picks: fmt(f.picks) })}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

// ── Forecast tab ────────────────────────────────────────────────────────────

function ShortCell({ d }: { d: PickForecastDay }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'WorkSummary' });
  if (!d.short) return <span className="text-neutral-400">–</span>;
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-x-1 text-red-700 font-medium">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="tabular-nums">{fmt(d.short)}</span>
      {d.extra_pickers != null && (
        <span className="text-xs font-normal text-red-600">{t('extraPickers', { count: d.extra_pickers, hours: fmt(d.extra_hours ?? 0) })}</span>
      )}
    </span>
  );
}

function Forecast({
  forecast, loading, error, days, onDays, farmSelect,
}: {
  forecast: PickForecast | null; loading: boolean; error: string | null; days: number;
  onDays: (n: number) => void; farmSelect: ReactNode;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'WorkSummary' });
  const labels = useMemo(() => forecast?.total.map((d) => dayName(d.date)) ?? [], [forecast]);
  const series: WorkSeries[] = useMemo(() => (forecast ? [
    { label: t('canPickByThen'), data: forecast.total.map((d) => d.capacity_to_date), colour: SERIES_COLOURS[2], dashed: true },
    { label: t('dueByThen'), data: forecast.total.map((d) => d.due_to_date), colour: SERIES_COLOURS[0], fillToPrevious: 'rgba(208, 59, 59, 0.12)' },
  ] : []), [forecast, t]);
  const dailySeries: WorkSeries[] = useMemo(() => (forecast ? [
    { label: t('due'), data: forecast.total.map((d) => d.due), colour: SERIES_COLOURS[0], kind: 'bar' },
    { label: t('canPick'), data: forecast.total.map((d) => d.capacity), colour: SERIES_COLOURS[2], kind: 'line', dashed: true },
  ] : []), [forecast, t]);

  const worst = forecast ? Math.max(0, ...forecast.total.map((d) => d.short)) : 0;
  const firstShort = forecast?.total.find((d) => d.short > 0);
  const totalPace = forecast?.farms.reduce((s, f) => s + (f.pace?.trees_per_day ?? 0), 0) ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="text-sm">
          <span className="block text-neutral-700">{t('lookAhead')}</span>
          <Segmented
            label={t('lookAhead')}
            value={String(days)}
            onChange={(v) => onDays(Number(v))}
            options={['7', '14', '28'].map((n) => ({ value: n, label: t('daysN', { n }) }))}
          />
        </div>
        {farmSelect}
      </div>
      <p className="text-xs text-neutral-500">
        {forecast ? t('forecastHow', { n: forecast.history.days }) : t('forecastHowShort')}
      </p>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {loading && !forecast && <div className="h-64 rounded-xl bg-neutral-100 animate-pulse" />}

      {forecast && (
        <div className={cn('space-y-5', loading && 'opacity-60')}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label={t('stillToPick')} value={fmt(forecast.to_pick)} sub={t('onLoads', { count: forecast.loads.length })} />
            <Tile label={t('overdue')} value={fmt(forecast.overdue)} tone={forecast.overdue ? 'bad' : undefined} sub={t('overdueSub')} />
            <Tile
              label={t('daysOfPicking')}
              value={totalPace ? fmt(Math.round((forecast.to_pick / totalPace) * 10) / 10) : '–'}
              sub={totalPace ? t('atPace', { n: fmt(Math.round(totalPace)) }) : t('noPace')}
            />
            <Tile
              label={t('worstShort')}
              value={worst ? fmt(worst) : '0'}
              tone={worst ? 'bad' : undefined}
              sub={firstShort ? t('firstShort', { date: dayName(firstShort.date) }) : (
                <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3.5 w-3.5" />{t('onTrack')}</span>
              )}
            />
          </div>

          <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
            <h2 className="text-base font-semibold text-neutral-900">{t('cumulativeTitle')}</h2>
            <p className="text-xs text-neutral-500">{t('cumulativeBody')}</p>
            <WorkChart labels={labels} series={series} ariaLabel={t('cumulativeTitle')} />
          </section>

          <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
            <h2 className="text-base font-semibold text-neutral-900">{t('dailyTitle')}</h2>
            <WorkChart labels={labels} series={dailySeries} height="h-44" ariaLabel={t('dailyTitle')} />
          </section>

          {forecast.farms.map((f) => (
            <section key={f.farm_id} className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold text-neutral-900 break-words">{f.name ?? t('unknownFarm')}</h2>
                {f.worst_short > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5" />{t('farmShort', { n: fmt(f.worst_short), date: dayName(f.first_short as string) })}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />{t('onTrack')}
                  </span>
                )}
              </div>
              <p className="text-sm text-neutral-600">
                {f.pace
                  ? t('paceLine', {
                    trees: fmt(f.pace.trees_per_day), days: f.pace.weekdays.map(weekdayName).join(', '),
                    pickers: fmt(f.pace.pickers_per_day), rate: fmt(f.pace.trees_per_picker_hour), best: fmt(f.pace.best_day),
                  })
                  : t('noPaceFarm')}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[28rem] text-sm">
                  <thead><tr className="text-left text-xs text-neutral-500">
                    <th className="py-1 font-medium">{t('day')}</th>
                    <th className="py-1 text-right font-medium">{t('due')}</th>
                    <th className="py-1 text-right font-medium">{t('canPick')}</th>
                    <th className="py-1 text-right font-medium">{t('behind')}</th>
                  </tr></thead>
                  <tbody>
                    {f.days.filter((d) => d.due > 0 || d.short > 0 || d.works).map((d) => (
                      <tr key={d.date} className="border-t border-neutral-100">
                        <td className="py-1">{dayName(d.date)}</td>
                        <td className="py-1 text-right tabular-nums">{d.due ? fmt(d.due) : '–'}</td>
                        <td className="py-1 text-right tabular-nums">{d.capacity ? fmt(d.capacity) : t('notPicking')}</td>
                        <td className="py-1 text-right"><ShortCell d={d} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
            <h2 className="text-base font-semibold text-neutral-900">{t('loadsTitle')}</h2>
            {forecast.loads.length === 0 ? <p className="text-sm text-neutral-500">{t('noLoads')}</p> : (
              <ul className="divide-y divide-neutral-100">
                {forecast.loads.map((l) => (
                  <li key={l.load_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <Link to={`/nursery/loads/${l.load_id}`} className="font-medium text-primary-700 hover:underline break-words">{l.load_number}</Link>
                      <p className="text-xs text-neutral-500 break-words">
                        {dayName(l.despatch_date)} · {l.farm_name ?? t('unknownFarm')} · {t('ordersN', { count: l.orders })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {l.overdue && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          <AlertTriangle className="h-3 w-3" />{t('overdueBadge')}
                        </span>
                      )}
                      <span className="text-sm tabular-nums">{t('toPickOf', { n: fmt(l.to_pick), of: fmt(l.ordered) })}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default WorkSummary;
